import { useState, useEffect, useRef } from "react";
import AdditionalChargesIcon from "../../SvgComponent/AdditionalCharges";
import styles from "../../../../../constants/styles.json";
import usePOSConfigStore from "../../../../../stores/usePOSConfigStore";
import useTransactionStore from "../../../../../stores/useTransactionStore";
import useCartStore from "../../../../../stores/useCartStore";
import { applyCurrencyRounding, getRoundingMethod, getPrecision } from "../../../../../utils/tillHelpers";

/**
 * Hook for additional charges / TCS / rounding logic.
 *
 * @param {Object} deps
 * @param {Object}   deps.cart
 * @param {Function} deps.setCart
 * @param {number}   deps.pricePrecision
 * @param {Object}   deps.tillData
 * @param {Object}   deps.posConfig
 * @param {Object}   deps.themeJSON
 */
const useAdditionalCharges = ({ cart, setCart, pricePrecision, tillData, posConfig, themeJSON }) => {
  const previousTcsRef = useRef(0);
  // BUG-1643: the TCS notification must fire once per order, not every time
  // the cart recalculates (e.g. rounding/total finalization while completing
  // the order). Tracks the sOrderID the popup was already shown for so later
  // recomputes of the same order — with the same or a different numeric
  // total — never reopen it; a genuinely new order (new sOrderID) can show it again.
  const tcsShownForOrderRef = useRef(null);
  const exchangeCart = useCartStore((s) => s.exchangeCart);

  // ── State ────────────────────────────────────────────────────────────
  const [additionalCharges, setAdditionalCharges] = useState(false);
  const [tempSaleTypeData, setTempSaleTypeData] = useState([]);
  const [serviceChargeEnabled, setServiceChargeEnabled] = useState(false);
  const [serviceChargePercentage, setServiceChargePercentage] = useState(5);
  const [tipMode, setTipMode] = useState("Percentage");
  const [tipPercentage, setTipPercentage] = useState(0);
  const [tipAmount, setTipAmount] = useState(0);
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [packingCharge, setPackingCharge] = useState(0);
  const [tcsModalAmount, setTcsModalAmount] = useState(0);
  const [showTcsModal, setShowTcsModal] = useState(false);
  const [roundingOption, setRoundingOption] = useState("No Rounding");

  const [serviceChargeAmount, setServiceChargeAmount] = useState(() => {
    return cart?.serviceCharge || 0;
  });

  const [tipTotal, setTipTotal] = useState(() => {
    return cart?.tip || 0;
  });

  const [deliveryChargeAmount, setDeliveryChargeAmount] = useState(() => {
    return cart?.deliveryCharge || 0;
  });

  const [packingChargeAmount, setPackingChargeAmount] = useState(() => {
    return cart?.packingCharge || 0;
  });

  // ── Helpers ──────────────────────────────────────────────────────────

  const toFixedTruncate = (num, decimalPlaces = 3) => {
    if (isNaN(num) || num === null) num = 0;
    const factor = Math.pow(10, decimalPlaces);
    const truncatedNum = Math.trunc(num * factor) / factor;
    const result = truncatedNum.toFixed(decimalPlaces);
    return result;
  };

  const applyRounding = (value, precision = pricePrecision) => {
    if (isNaN(value) || value === null) return 0;
    const factor = Math.pow(10, precision);
    switch (roundingOption) {
      case "Round Up":
        return Math.ceil(value * factor) / factor;
      case "Round Down":
        return Math.floor(value * factor) / factor;
      case "No Rounding":
      default:
        return parseFloat(toFixedTruncate(value, precision));
    }
  };

  const compare = (lhs, operator, rhs) => {
    switch (operator) {
      case ">":
        return lhs > rhs;
      case "<":
        return lhs < rhs;
      case "=":
        return lhs == rhs;
      default:
        return false;
    }
  };

  const calculateCharge = (charge, subtotal) => {
    if (!charge) return 0;

    if (charge.value !== null) {
      if (charge.isPercentage) {
        return (subtotal * charge.value) / 100;
      }
      return charge.value;
    }

    // default fallback
    if (charge.sameTypeCount === 1) {
      if (charge.isPercentage && charge.defaultPercentage > 0) {
        return (subtotal * charge.defaultPercentage) / 100;
      }
      return charge.defaultAmount || 0;
    }

    return 0;
  };

  // ── Core functions ───────────────────────────────────────────────────

  // Shared item-wise TCS calculation, used by every TCS branch (PAN and NRI alike).
  // Rate/eligibility resolution legitimately differs per customer type and stays in
  // each branch below; this is only the "apply a resolved rate to the cart" step,
  // which is identical regardless of how that rate was determined.
  const defaultTcsItemValue = (item) => item.lineGross || item.linegross || item.nettotal || 0;

  const applyItemWiseTcs = (items, rate, { getItemValue = defaultTcsItemValue, filter } = {}) => {
    const updatedItems = (items || []).map((item) => {
      const cleaned = { ...item };
      delete cleaned.itemTcs;
      return cleaned;
    });

    let totalTcs = 0;
    let hasEligibleItems = false;

    updatedItems.forEach((item) => {
      const itemValue = getItemValue(item);
      if (filter && !filter(itemValue)) return;
      const itemTcs = applyRounding(itemValue * rate);
      totalTcs += itemTcs;
      item.itemTcs = itemTcs;
      hasEligibleItems = true;
    });

    return { total: applyRounding(totalTcs), items: updatedItems, hasEligibleItems };
  };

  // Resolve the value a `when.field` refers to, for whichever customer is on the cart.
  // Adding a future customer type/field means adding one case here, not a new branch
  // of computeTCS() and not a second copy of rate/applicability resolution.
  const resolveCustomerFieldValue = (field, cartData) => {
    switch (field) {
      case "customer.pan_status":
        return cartData?.customer?.customerProperties?.realStatus?.trim()?.toUpperCase() || null;
      case "customer.customer_type":
        return cartData?.customer?.nriBilling === true ? "NRI" : null;
      default:
        return null;
    }
  };

  // A condition with no `when` is universal (matches any customer). A condition with a
  // `when` only matches if that customer's resolved field value is in its value list.
  const matchesWhen = (when, cartData) => {
    if (!when) return true;
    if (when.operator !== "IN") return false;
    const resolved = resolveCustomerFieldValue(when.field, cartData);
    if (resolved === null) return false;
    const normalizedValues = (when.value || [])
      .filter((v) => v !== null && v !== undefined)
      .map((v) => v.toString().trim().toUpperCase());
    return normalizedValues.includes(resolved.toString().trim().toUpperCase());
  };

  const computeTCS = (cartData, tempSaleTypeData) => {
    if (!cartData?.customer) return { total: 0, eligible: false, items: null };

    const isNRI = cartData?.customer?.nriBilling === true;

    const tcsCharge = tempSaleTypeData?.find((c) => c.chargeType === "TCS");
    if (!tcsCharge || !tcsCharge.conditionsFormula) return { total: 0, eligible: false, items: null };

    let formula;
    try {
      formula = JSON.parse(tcsCharge.conditionsFormula);
    } catch {
      return { total: 0, eligible: false, items: null };
    }

    // Rate: first rate_determination condition whose `when` matches this customer wins.
    let rate = 0;
    if (formula?.rate_determination?.type === "conditional") {
      for (const cond of formula.rate_determination.conditions || []) {
        if (matchesWhen(cond.when, cartData)) {
          rate = Number(cond.rate) || 0;
          break;
        }
      }
    }
    // Legacy top-level override, kept for configs authored before conditions covered NRI.
    if (isNRI && formula?.nri_rate !== undefined) {
      rate = Number(formula.nri_rate) || rate;
    }
    if (rate === 0) return { total: 0, eligible: false, items: null };

    // Applicability: same first-match-wins rule, evaluated the same way for every customer.
    const applicabilityConditions = formula?.applicability?.conditions || [];
    let applicability = null;
    for (const cond of applicabilityConditions) {
      if (matchesWhen(cond.when, cartData)) {
        applicability = cond;
        break;
      }
    }
    if (!applicability) {
      applicability = applicabilityConditions[0];
    }
    // Legacy compatibility: configs authored with only nri_rate/nri_threshold and no
    // applicability.conditions at all used to gate on subTotal+tax alone. Keep that working.
    if (!applicability && isNRI && formula?.nri_threshold !== undefined) {
      applicability = { field: "__legacy_nri_base_total", operator: ">", value: Number(formula.nri_threshold) };
    }
    if (!applicability) return { total: 0, eligible: false, items: null };

    // Legacy top-level override for the matched condition's threshold value.
    const applicabilityValue =
      isNRI && formula?.nri_threshold !== undefined ? Number(formula.nri_threshold) || applicability.value : applicability.value;

    let result = { total: 0, items: [], hasEligibleItems: false };
    let subtotalTrigger = false;

    if (applicability?.field === "cart.total") {
      const cartTotal =
        (cartData?.subTotal || 0) +
        (cartData?.tax || 0) +
        (cartData?.serviceCharge || 0) +
        (cartData?.tip || 0) +
        (cartData?.deliveryCharge || 0) +
        (cartData?.others || 0) +
        (cartData?.packingCharge || 0);
      if (!compare(cartTotal, applicability.operator, applicabilityValue)) {
        return { total: 0, eligible: false, items: null };
      }
      result = applyItemWiseTcs(cartData.items, rate);
    } else if (applicability?.field === "cart.bill_item_value") {
      result = applyItemWiseTcs(cartData.items, rate, {
        getItemValue: (item) => item.lineGross || item.linegross || parseFloat(item.realPrice || item.sunitprice || 0) * parseFloat(item.weight || 0) || 0,
        filter: (itemValue) => compare(itemValue, applicability.operator, applicabilityValue),
      });
    } else if (applicability?.field === "__legacy_nri_base_total") {
      const baseTotal = (cartData?.subTotal || 0) + (cartData?.tax || 0);
      if (!compare(baseTotal, applicability.operator, applicabilityValue)) {
        return { total: 0, eligible: false, items: null };
      }
      result = applyItemWiseTcs(cartData.items, rate);
    } else {
      const subtotal = cartData?.subTotal || 0;
      if (compare(subtotal, applicability.operator, applicabilityValue)) {
        subtotalTrigger = true;
        result = applyItemWiseTcs(cartData.items, rate);
      }
    }

    return {
      total: result.total,
      eligible: result.hasEligibleItems || subtotalTrigger,
      items: result.hasEligibleItems ? result.items : null,
    };
  };

  const handleApplyCharges = () => {
    setAdditionalCharges(false);

    const chargeMap = {};

    tempSaleTypeData.forEach((charge, index, array) => {
      const chargeType = charge.chargeType;
      const sameTypeCount = array.filter((c) => c.chargeType === chargeType).length;

      let uiValue = null;

      if (chargeType === "SERVICE_CHARGE") {
        uiValue = serviceChargeEnabled ? serviceChargePercentage : null;
      }
      if (chargeType === "TIP") {
        uiValue = tipMode === "Percentage" ? tipPercentage : tipAmount;
      }
      if (chargeType === "DELIVERY_FEE") {
        uiValue = deliveryCharge;
      }
      if (chargeType === "PACKING_FEE") {
        uiValue = packingCharge;
      }

      chargeMap[chargeType] = {
        value: uiValue !== null && uiValue !== "" ? Number(uiValue) : null,
        defaultAmount: charge.defaultAmount,
        defaultPercentage: charge.defaultPercentage,
        isPercentage:
          (chargeType === "SERVICE_CHARGE" && serviceChargeEnabled) ||
          (chargeType === "TIP" && tipMode === "Percentage") ||
          (charge.defaultPercentage > 0 && charge.defaultAmount === 0),
        sameTypeCount: sameTypeCount,
      };
    });

    const subtotal = cart?.subTotal || 0;
    const tax = cart.tax || 0;

    const serviceChargeAmt = calculateCharge(chargeMap["SERVICE_CHARGE"], subtotal);
    const tipAmt = calculateCharge(chargeMap["TIP"], subtotal);
    const deliveryAmt = calculateCharge(chargeMap["DELIVERY_FEE"], subtotal);
    const packingAmt = calculateCharge(chargeMap["PACKING_FEE"], subtotal);

    const tempServiceCharge = applyRounding(serviceChargeAmt);
    const tempTip = applyRounding(tipAmt);
    const tempDelivery = applyRounding(deliveryAmt);
    const tempPacking = applyRounding(packingAmt);

    const grossTotal = parseFloat(subtotal) + parseFloat(tax) + tempServiceCharge + tempTip + tempDelivery + tempPacking + (cart.tcs || 0);
    const { roundedTotal: roundedGrossTotal, roundOffAmount: chargesRoundOff } = applyCurrencyRounding(grossTotal, getRoundingMethod(tillData), getPrecision(tillData));
    const updatedCart = {
      ...cart,
      serviceCharge: tempServiceCharge,
      tip: tempTip,
      deliveryCharge: tempDelivery,
      packingCharge: tempPacking,
      total: roundedGrossTotal,
      roundOff: chargesRoundOff,
    };

    setCart(updatedCart);
  };

  const getChargeLabel = (charge, cart) => {
    // Special case for TCS
    if (charge.chargeType === "TCS") {
      const tcsValue = cart?.tcs ? Number(cart.tcs).toFixed(2) : "0";
      return `TCS - ${tcsValue}`;
    }

    // Normal UI display name logic
    try {
      if (charge.conditionsFormula) {
        const parsed = JSON.parse(charge.conditionsFormula);
        if (parsed?.ui?.display_name) {
          return parsed.ui.display_name;
        }
      }
    } catch (err) {
      console.error("[AdditionalCharges] Failed to parse conditionsFormula for display name:", err);
    }

    // Fallback formatting
    const label = charge.chargeType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return label;
  };

  const renderAdditionalChargesButton = () => {
    if (!tempSaleTypeData || tempSaleTypeData.length === 0) return null;

    return (
      <div
        style={{
          width: "100%",
          marginBottom: "0.37rem",
        }}
      >
        <button
          className="cartMenuItems"
          id="sm-parked-bill"
          style={{
            backgroundColor: themeJSON.cartMenuItemsBGColor || "#2F3856",
            border: `1px solid ${themeJSON.cartMenuItemsBGColor || "#2F3856"}`,
            borderRadius: "7px",
            height: "9.5vh",
          }}
          onClick={() => setAdditionalCharges(true)}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <AdditionalChargesIcon color={"#fff"} />
            <span style={styles.screenStyles.mainScreen.posIcon}>Additional Charges</span>
          </div>
        </button>
      </div>
    );
  };

  // ── Effects ──────────────────────────────────────────────────────────

  // Poll saleTypeData from store and initialise tempSaleTypeData + totals
  useEffect(() => {
    const interval = setInterval(() => {
      const parsed = usePOSConfigStore.getState().saleTypeData;
      if (parsed) {
        let updatedTempSaleTypeData = parsed?.additionalCharges || [];
        if (updatedTempSaleTypeData?.length > 0) {
          const localCart = {
            ...cart,
            serviceCharge: cart.serviceCharge ?? 0,
            tip: cart.tip ?? 0,
            deliveryCharge: cart.deliveryCharge ?? 0,
            others: cart.others ?? 0,
            packingCharge: cart.packingCharge ?? 0,
            tcs: cart.tcs ?? 0,
            tax: cart.tax ?? 0,
            total: cart.total ?? 0,
          };
          updatedTempSaleTypeData = updatedTempSaleTypeData.map((charge, index, array) => {
            const sameTypeCount = array.filter((c) => c.chargeType === charge.chargeType).length;
            const subtotal = cart?.subTotal ?? 0;
            const localValue =
              localCart[
                charge.chargeType === "SERVICE_CHARGE"
                  ? "serviceCharge"
                  : charge.chargeType === "TIP"
                    ? "tip"
                    : charge.chargeType === "DELIVERY_FEE"
                      ? "deliveryCharge"
                      : charge.chargeType === "OTHERS"
                        ? "others"
                        : charge.chargeType === "PACKING_FEE"
                          ? "packingCharge"
                          : charge.chargeType === "TCS"
                            ? "tcs"
                            : null
              ] || 0;
            const isPercentageMatch = charge.defaultPercentage > 0 && Math.abs(localValue - (subtotal * charge.defaultPercentage) / 100) < 0.01;

            let newValue;
            if (localValue > 0) {
              newValue = isPercentageMatch ? charge.defaultPercentage : localValue;
            } else if (sameTypeCount > 1 && localValue === 0) {
              newValue = "";
            } else {
              newValue = charge.value ?? (charge.defaultAmount !== 0 ? charge.defaultAmount : charge.defaultPercentage);
            }

            switch (charge.chargeType) {
              case "SERVICE_CHARGE":
              case "TIP":
              case "DELIVERY_FEE":
              case "OTHERS":
              case "PACKING_FEE":
              case "TCS":
                return { ...charge, value: newValue };
              default:
                return charge;
            }
          });

          setTempSaleTypeData(updatedTempSaleTypeData);

          const discount = localCart.discount;
          let updatedTotal;

          if (localCart.isReturn === true) {
            const returnTotal = localCart.items.reduce((sum, item) => sum + (item.nettotal ?? 0), 0);
            // Derive return tax from the same line items (item.taxAmount carries the
            // negated per-line tax). returnTotal is gross (tax-inclusive), so the
            // tax-exclusive subtotal is returnTotal - returnTax. Keeps total unchanged.
            const returnTax = localCart.items.reduce((sum, item) => sum + (parseFloat(item.taxAmount) || 0), 0);
            const existingReturnTcs = localCart.tcs ? -Math.abs(localCart.tcs) : 0;

            const cleanedCart = {
              ...localCart,
              subTotal: returnTotal - returnTax,
              tax: returnTax,
              serviceCharge: 0,
              tip: 0,
              deliveryCharge: 0,
              others: 0,
              packingCharge: 0,
              tcs: existingReturnTcs,
              total: returnTotal + existingReturnTcs,
            };
            setCart(cleanedCart);
            clearInterval(interval);
            return;
          } else {
            // Build total from base fields to avoid double-counting
            updatedTotal =
              (localCart.subTotal || 0) +
              (localCart.tax || 0) +
              localCart.serviceCharge +
              localCart.tip +
              localCart.deliveryCharge +
              localCart.others +
              localCart.packingCharge +
              (localCart.tcs ?? 0);
          }
          const { roundedTotal: roundedUpdatedTotal, roundOffAmount: updatedRoundOff } = applyCurrencyRounding(updatedTotal, getRoundingMethod(tillData), getPrecision(tillData));
          if (Math.abs((localCart.total || 0) - roundedUpdatedTotal) > 0.01 && (localCart?.isReturn !== true || localCart?.isLayaway !== "Y")) {
            setCart({ ...localCart, total: roundedUpdatedTotal, roundOff: updatedRoundOff });
          }
        }

        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [cart]);

  const selectedReturnType = useTransactionStore((s) => s.selectedReturnType);

  const activeCart = selectedReturnType === "EXCHANGE" ? exchangeCart : cart;
  // Recompute TCS and total whenever items / customer / charges change
  useEffect(() => {
    if (!Array.isArray(tempSaleTypeData) || tempSaleTypeData.length === 0) {
      return;
    }

    const selectedReturnType = useTransactionStore.getState().selectedReturnType;

    const isExchangeMode = selectedReturnType === "EXCHANGE";

    const activeCart = isExchangeMode ? useCartStore.getState().exchangeCart : useCartStore.getState().cart;

    if (!activeCart || !Array.isArray(activeCart.items)) {
      return;
    }

    if (activeCart?.isReturn) {
      return;
    }

    const { total: tcsValue, items: updatedItems } = computeTCS(activeCart, tempSaleTypeData);

    const updatedTotal =
      (activeCart.subTotal || 0) +
      (activeCart.tax || 0) +
      (activeCart.serviceCharge || 0) +
      (activeCart.tip || 0) +
      (activeCart.deliveryCharge || 0) +
      (activeCart.others || 0) +
      (activeCart.packingCharge || 0) +
      tcsValue;

    const tcsChanged = activeCart.tcs !== tcsValue;

    const { roundedTotal: roundedUpdatedTotal, roundOffAmount: tcsRoundOff } = applyCurrencyRounding(updatedTotal, getRoundingMethod(tillData), getPrecision(tillData));
    const totalChanged = Math.abs((activeCart.total || 0) - roundedUpdatedTotal) > 0.01;

    if (!tcsChanged && !totalChanged) {
      return;
    }

    const updatedCart = {
      ...activeCart,
      items: updatedItems || activeCart.items,
      tcs: tcsValue,
      total: roundedUpdatedTotal,
      roundOff: tcsRoundOff,
    };

    if (isExchangeMode) {
      useCartStore.getState().setExchangeCart(updatedCart);
    } else {
      setCart(updatedCart);
    }
  }, [
    activeCart?.items,
    activeCart?.subTotal,
    activeCart?.tax,
    activeCart?.discount,
    activeCart?.customer,
    activeCart?.serviceCharge,
    activeCart?.tip,
    activeCart?.deliveryCharge,
    activeCart?.others,
    activeCart?.packingCharge,
    tempSaleTypeData,
    selectedReturnType,
  ]);

  // Show TCS notification modal on change
  useEffect(() => {
    const selectedReturnType = useTransactionStore.getState().selectedReturnType;

    const activeCart = selectedReturnType === "EXCHANGE" ? useCartStore.getState().exchangeCart : useCartStore.getState().cart;

    if (!activeCart) return;

    if (activeCart?.isReturn) return;

    const { total, eligible } = computeTCS(activeCart, tempSaleTypeData);

    const prev = previousTcsRef.current;

    if (eligible && total > 0 && total !== prev && tcsShownForOrderRef.current !== activeCart?.sOrderID) {
      setTcsModalAmount(total);

      setShowTcsModal(true);

      tcsShownForOrderRef.current = activeCart?.sOrderID;

      setTimeout(() => {
        setShowTcsModal(false);
      }, 5000);
    }

    previousTcsRef.current = total;
  }, [activeCart?.items, activeCart?.customer, activeCart?.subTotal, activeCart?.tax, activeCart?.discount, tempSaleTypeData, selectedReturnType]);

  // ── Return ───────────────────────────────────────────────────────────
  return {
    // state
    additionalCharges,
    setAdditionalCharges,
    tempSaleTypeData,
    setTempSaleTypeData,
    serviceChargeEnabled,
    setServiceChargeEnabled,
    serviceChargePercentage,
    setServiceChargePercentage,
    tipMode,
    setTipMode,
    tipPercentage,
    setTipPercentage,
    tipAmount,
    setTipAmount,
    deliveryCharge,
    setDeliveryCharge,
    packingCharge,
    setPackingCharge,
    tcsModalAmount,
    setTcsModalAmount,
    showTcsModal,
    setShowTcsModal,
    roundingOption,
    setRoundingOption,
    serviceChargeAmount,
    setServiceChargeAmount,
    tipTotal,
    setTipTotal,
    deliveryChargeAmount,
    setDeliveryChargeAmount,
    packingChargeAmount,
    setPackingChargeAmount,

    // functions
    toFixedTruncate,
    applyRounding,
    compare,
    calculateCharge,
    computeTCS,
    handleApplyCharges,
    getChargeLabel,
    renderAdditionalChargesButton,
  };
};

export default useAdditionalCharges;
